import { useCallback, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { ShowSuccessFetch, showSuccess, showWarning } from "src/utils/toast/toastUtil";

import {
    deleteSYS1020_R01,
    downloadExcel_SYS1020_R01,
    getSYS1020_R01AllCount,
    getSYS1020_R01List,
    patchSYS1020_R01,
    postSYS1020_R01,
} from "./Api_SYS1020_R01";
import { defaultSYS1020_R01Dto, SYS1020_R01Dto } from "./Dto_SYS1020_R01";
import { SYS1020_R01Filter, useSYS1020_R01Store } from "./Store_SYS1020_R01";

interface ContentProps {
    gridRef: React.RefObject<AgGridReact<SYS1020_R01Dto> | null>;
    setMessage: (message: string) => void;
}

export const Content_SYS1020_R01 = ({ gridRef, setMessage }: ContentProps) => {
    const {
        SYS1020_R01Filter,
        setSYS1020_R01PastFilter,
        SYS1020_R01List,
        setSYS1020_R01List,
        insertSYS1020_R01List,
        deletedSYS1020_R01List,
        addDeletedSYS1020_R01List,
        removeDeletedSYS1020_R01List,
        selectedSYS1020_R01List,
        setSelectedSYS1020_R01List,
        removeSelectedSYS1020_R01List,
        setOriginalSYS1020_R01List,
        setAllCount,
        setScrollTop,
        initData,
    } = useSYS1020_R01Store();
    const { messageSrcList, messageTypeList } = useCommonCodeStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const [clickable, setClickable] = useState(true);

    useFetchActionbarAuth("SYS1020_R01");

    const getParams = useCallback((filter: SYS1020_R01Filter): Partial<SYS1020_R01Filter> => ({
        messageSrc: filter.messageSrc || undefined,
        messageType: filter.messageType || undefined,
    }), []);

    const executeDataFetch = useCallback(async (
        fetchType: "search" | "refetch",
        showFetchMessage = true,
    ) => {
        setLoadingSpinner(true);
        try {
            if (fetchType === "search") setScrollTop(null);
            const params = getParams(SYS1020_R01Filter);
            const [listResponse, countResponse] = await Promise.all([
                getSYS1020_R01List(fetchType, params),
                getSYS1020_R01AllCount(params),
            ]);
            const list = listResponse.data.data;
            setSYS1020_R01List(list);
            setOriginalSYS1020_R01List(list);
            setSelectedSYS1020_R01List([]);
            removeDeletedSYS1020_R01List();
            setAllCount(countResponse.data.data);
            setSYS1020_R01PastFilter(SYS1020_R01Filter);
            gridRef.current?.api.ensureIndexVisible(0, "top");

            if (showFetchMessage) {
                if (list.length > 0) {
                    ShowSuccessFetch(
                        setMessage,
                        `조회가 완료되었습니다. (총 ${list.length}/${countResponse.data.data}건)`,
                    );
                    window.setTimeout(() => setMessage(""), 5000);
                } else {
                    showWarning("데이터가 없습니다.");
                }
            }
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        SYS1020_R01Filter,
        getParams,
        gridRef,
        removeDeletedSYS1020_R01List,
        setAllCount,
        setLoadingSpinner,
        setMessage,
        setOriginalSYS1020_R01List,
        setSYS1020_R01List,
        setSYS1020_R01PastFilter,
        setScrollTop,
        setSelectedSYS1020_R01List,
    ]);

    const fetchData = useCallback(() => {
        void executeDataFetch("search");
    }, [executeDataFetch]);

    const defaultSYS1020_R01Row = useCallback((): SYS1020_R01Dto => ({
        ...defaultSYS1020_R01Dto,
        messageSrc: messageSrcList[0]?.minorCd ?? "",
        messageType: messageTypeList[0]?.minorCd ?? "",
        inserted: true,
    }), [messageSrcList, messageTypeList]);

    const handleInsertClick = useCallback((event: React.MouseEvent, rowIndex?: number) => {
        const isGridInsert = rowIndex !== undefined;
        InsertRowHandler({
            event,
            dataList: SYS1020_R01List,
            rowData: isGridInsert
                ? SYS1020_R01List[rowIndex]
                : { ...defaultSYS1020_R01Row(), valueIndex: -1 },
            insertRow: insertSYS1020_R01List,
            defaultRowData: defaultSYS1020_R01Row(),
            ...(isGridInsert && { setClickable }),
        });
    }, [SYS1020_R01List, defaultSYS1020_R01Row, insertSYS1020_R01List]);

    const validateRows = useCallback((rows: SYS1020_R01Dto[]) => {
        const activeRows = SYS1020_R01List.filter(
            (row) => !deletedSYS1020_R01List.some((id) => Number(id) === row.valueIndex),
        );
        const keySet = new Set<string>();

        for (const row of rows) {
            if (!row.messageSrc.trim()) {
                showWarning("발생구분을 선택해주세요.");
                return false;
            }
            if (!row.messageType.trim()) {
                showWarning("메시지구분을 선택해주세요.");
                return false;
            }
            if (!row.messageCd.trim()) {
                showWarning("메시지코드를 입력해주세요.");
                return false;
            }
            if (!row.messageNm.trim()) {
                showWarning(`메시지명을 입력해주세요.\n메시지코드: ${row.messageCd}`);
                return false;
            }
        }

        for (const row of activeRows) {
            const key = `${row.messageType}\u0000${row.messageSrc}\u0000${row.messageCd}`;
            if (keySet.has(key)) {
                showWarning(`중복된 메시지 키가 있습니다.\n메시지코드: ${row.messageCd}`);
                return false;
            }
            keySet.add(key);
        }

        return true;
    }, [SYS1020_R01List, deletedSYS1020_R01List]);

    const handleSaveClick = useCallback(async () => {
        const insertedList = SYS1020_R01List.filter(
            (row) => row.inserted
                && !deletedSYS1020_R01List.some((id) => Number(id) === row.valueIndex),
        );
        const modifiedList = SYS1020_R01List.filter(
            (row) => row.isModified && !row.inserted
                && !deletedSYS1020_R01List.some((id) => Number(id) === row.valueIndex),
        );
        const deleteTargetList = SYS1020_R01List.filter(
            (row) => deletedSYS1020_R01List.some((id) => Number(id) === row.valueIndex),
        );

        if (insertedList.length === 0 && modifiedList.length === 0 && deleteTargetList.length === 0) {
            showWarning("저장할 변경사항이 없습니다.");
            return;
        }
        if (!validateRows([...insertedList, ...modifiedList])) return;

        setLoadingSpinner(true);
        try {
            await Promise.all([
                insertedList.length > 0 ? postSYS1020_R01(insertedList) : Promise.resolve(),
                modifiedList.length > 0 ? patchSYS1020_R01(modifiedList) : Promise.resolve(),
                ...deleteTargetList.map((row) => deleteSYS1020_R01(row)),
            ]);
            showSuccess("메시지 저장이 완료되었습니다.");
            setMessage("메시지 저장이 완료되었습니다.");
            window.setTimeout(() => setMessage(""), 5000);
            await executeDataFetch("refetch", false);
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        SYS1020_R01List,
        deletedSYS1020_R01List,
        executeDataFetch,
        setLoadingSpinner,
        setMessage,
        validateRows,
    ]);

    const handleDeleteClick = useCallback(() => {
        if (deletedSYS1020_R01List.length > 0) {
            removeDeletedSYS1020_R01List();
            removeSelectedSYS1020_R01List();
            setSYS1020_R01List([...SYS1020_R01List]);
            return;
        }

        if (selectedSYS1020_R01List.length === 0) {
            showWarning("삭제할 행을 선택해주세요.");
            return;
        }

        const insertedValueIndexes = new Set(
            SYS1020_R01List
                .filter((row) => row.inserted
                    && selectedSYS1020_R01List.some((id) => Number(id) === row.valueIndex))
                .map((row) => row.valueIndex),
        );
        setSYS1020_R01List(
            SYS1020_R01List.filter((row) => !insertedValueIndexes.has(row.valueIndex)),
        );
        addDeletedSYS1020_R01List(selectedSYS1020_R01List);
        removeSelectedSYS1020_R01List();
    }, [
        SYS1020_R01List,
        addDeletedSYS1020_R01List,
        deletedSYS1020_R01List,
        removeDeletedSYS1020_R01List,
        removeSelectedSYS1020_R01List,
        selectedSYS1020_R01List,
        setSYS1020_R01List,
    ]);

    const handleExcelClick = useCallback(async () => {
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_SYS1020_R01(getParams(SYS1020_R01Filter));
            downloadSettingExcel({
                responseData: response.data.data,
                fileName: "메시지 등록-excel.xlsx",
            });
        } finally {
            setLoadingSpinner(false);
        }
    }, [SYS1020_R01Filter, getParams, setLoadingSpinner]);

    const initContainerData = useCallback(() => {
        initData();
        setMessage("");
    }, [initData, setMessage]);

    return {
        clickable,
        setClickable,
        fetchData,
        handleInsertClick,
        handleDeleteClick,
        handleSaveClick,
        handleExcelClick,
        initContainerData,
        defaultSYS1020_R01Row,
    };
};
